import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DocumentViewer from "./DocumentViewer";
import {
  Loader,
  AlertTriangle,
  CheckCircle,
  Eye,
  AlertCircle,
  X,
  Calendar,
  User,
  FileText,
} from "lucide-react";
import { DocumentViewerModal } from "./DocumentViewerModal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import kycService from "@/services/api/kyc.service";

// RejectionCommentModal moved outside to fix focus issues
const RejectionCommentModal = ({
  isOpen,
  onClose,
  onReject,
  isSubmitting,
  initialComment
}) => {
  const [localComment, setLocalComment] = useState("");

  useEffect(() => {
    if (isOpen) {
      setLocalComment(initialComment || "");
    }
  }, [isOpen, initialComment]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
        <h3 className="text-lg font-medium mb-4">Provide Rejection Reason</h3>
        <textarea
          value={localComment}
          onChange={(e) => setLocalComment(e.target.value)}
          className="w-full h-32 border border-gray-300 rounded p-2 mb-4"
          placeholder="Please explain why this KYC application is being rejected (minimum 3 characters)..."
        />
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={() => onReject(localComment)}
            disabled={
              !localComment.trim() ||
              localComment.trim().length < 3 ||
              isSubmitting
            }
            className={`px-4 py-2 rounded text-white ${localComment.trim() &&
              localComment.trim().length >= 3 &&
              !isSubmitting
              ? "bg-red-600 hover:bg-red-700"
              : "bg-red-300 cursor-not-allowed"
              }`}
          >
            {isSubmitting ? "Rejecting..." : "Reject Application"}
          </button>
        </div>
      </div>
    </div>
  );
};

const UserKycView = ({ id }) => {
  const [kycDetails, setKycDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rejectionComment, setRejectionComment] = useState("");
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [activeDocument, setActiveDocument] = useState(null);
  const [showVerifiedData, setShowVerifiedData] = useState(false);
  const [activeDocumentModal, setActiveDocumentModal] = useState({
    isOpen: false,
    documentData: null,
    documentName: "",
  });

  // Separate loading states for each button
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [inReviewLoading, setInReviewLoading] = useState(false);

  // Edit states
  const [isEditing, setIsEditing] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({
    panNumber: "",
    dateOfBirth: "",
    nationality: "",
    address: { line1: "", line2: "", city: "", state: "", country: "", pincode: "" },
    bankDetails: { bankName: "", accountName: "", accountNumber: "", ifscCode: "", accountType: "savings" },
    dematDetails: { dpId: "", clientId: "", depository: "NSDL", dan: "" },
  });
  
  const [validationErrors, setValidationErrors] = useState({
    panNumber: "",
    bankName: "",
    accountName: "",
    accountNumber: "",
    ifscCode: "",
    dpId: "",
    clientId: "",
  });

  useEffect(() => {
    fetchKycDetails();
  }, [id]);

  const fetchKycDetails = async () => {
    setLoading(true);
    try {
      const response = await kycService.getKycDetails(id);

      if (response.status === "success") {
        setKycDetails(response.data.kyc);
      } else {
        throw new Error(response.message || "Failed to fetch KYC details");
      }
    } catch (err) {
      console.error("Error fetching KYC details:", err);
      setError(err.message || "An error occurred while fetching KYC details");
      toast.error(
        err.message || "An error occurred while fetching KYC details",
        {
          position: "bottom-right",
          autoClose: 5000,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  // Enhanced error handling utility with toast notifications
  const handleApiError = (error, errorType) => {
    console.error(`${errorType} error:`, error);

    // Show error toast notification
    toast.error(error.message || "An unexpected error occurred", {
      position: "bottom-right",
      autoClose: 5000,
    });
  };

  // Handle verify KYC
  const handleVerifyKyc = async () => {
    setVerifyLoading(true);

    try {
      const response = await kycService.verifyKyc(id);

      if (response.success) {
        toast.success("KYC application verified successfully", {
          position: "bottom-right",
          autoClose: 3000,
        });
        // Refresh the details
        await fetchKycDetails();
      } else {
        throw new Error(response.message || "Failed to verify KYC");
      }
    } catch (err) {
      handleApiError(err, "verifyError");
    } finally {
      setVerifyLoading(false);
    }
  };

  // Handle rejection with comment
  const handleRejectWithComment = async (comment) => {
    const commentToUse = comment || rejectionComment;
    if (!commentToUse?.trim()) return;

    setRejectLoading(true);

    try {
      const response = await kycService.rejectKyc(id, commentToUse);

      if (response.status === "success") {
        toast.success("KYC application rejected successfully", {
          position: "bottom-right",
          autoClose: 3000,
        });
        setCommentModalOpen(false);
        setRejectionComment("");

        // Refresh the details
        await fetchKycDetails();
      } else {
        throw new Error(response.message || "Failed to reject KYC");
      }
    } catch (err) {
      handleApiError(err, "rejectError");
    } finally {
      setRejectLoading(false);
    }
  };

  // Handle mark as in-review
  const handleMarkInReview = async () => {
    setInReviewLoading(true);

    try {
      const response = await kycService.markInReview(id);

      if (response.success) {
        toast.success("KYC application marked as in-review successfully", {
          position: "bottom-right",
          autoClose: 3000,
        });
        // Refresh the details
        await fetchKycDetails();
      } else {
        throw new Error(response.message || "Failed to mark as in-review");
      }
    } catch (err) {
      handleApiError(err, "markInReviewError");
    } finally {
      setInReviewLoading(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    if (newStatus === kycDetails.verificationStatus) {
      return; // No change needed
    }

    switch (newStatus) {
      case "verified":
        await handleVerifyKyc();
        break;
      case "rejected":
        setCommentModalOpen(true);
        break;
      case "in-review":
        await handleMarkInReview();
        break;
      default:
        toast.error("Invalid status selection", {
          position: "bottom-right",
          autoClose: 5000,
        });
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "verified":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "in-review":
        return "bg-blue-100 text-blue-800";
      case "pending":
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const documentTypes = {
    panCard: "PAN Card",
    bankDocuments: "Bank Documents",
    dematDocuments: "Demat Documents",
    aadhaarFront: "Aadhaar Front",
    aadhaarBack: "Aadhaar Back",
  };

  // Get current data or verified data based on toggle
  const getCurrentData = () => {
    if (showVerifiedData && kycDetails?.verifiedDoc) {
      return kycDetails.verifiedDoc;
    }
    return kycDetails;
  };

  const validateField = (section, field, value, extraParam = null) => {
    let error = "";
    if (section === null && field === "panNumber") {
      if (value && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) {
        error = "Invalid PAN. Format: 5 Letters, 4 Digits, 1 Letter (e.g. ABCDE1234F)";
      }
    } else if (section === "bankDetails") {
      if (field === "bankName" && value && value.length < 3) {
        error = "Bank name must be at least 3 characters";
      } else if (field === "accountName" && value && value.length < 3) {
        error = "Account name must be at least 3 characters";
      } else if (field === "accountNumber" && value && !/^\d{9,18}$/.test(value)) {
        error = "Account number must be 9-18 digits";
      } else if (field === "ifscCode" && value && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) {
        error = "Format: 4 letters, '0', 6 alphanumeric (e.g. SBIN0001234)";
      }
    } else if (section === "dematDetails") {
      const depo = extraParam || editFormData.dematDetails.depository;
      if (field === "dpId" && value) {
        if (depo === "NSDL" && !/^IN\d{6}$/.test(value)) {
          error = 'NSDL DP ID must be "IN" + 6 digits (e.g. IN300123)';
        } else if (depo === "CDSL" && !/^\d{8}$/.test(value)) {
          error = "CDSL DP ID must be 8 digits";
        }
      } else if (field === "clientId" && value && !/^\d{8}$/.test(value)) {
        error = "Client ID must be 8 digits";
      }
    }
    return error;
  };

  const handleEditClick = () => {
    const data = getCurrentData() || {};
    setEditFormData({
      panNumber: data.panNumber || "",
      dateOfBirth: data.dateOfBirth ? new Date(data.dateOfBirth).toISOString().split('T')[0] : "",
      nationality: data.nationality || "",
      address: {
        line1: data.address?.line1 || "",
        line2: data.address?.line2 || "",
        city: data.address?.city || "",
        state: data.address?.state || "",
        country: data.address?.country || "",
        pincode: data.address?.pincode || "",
      },
      bankDetails: {
        bankName: data.bankDetails?.bankName || "",
        accountName: data.bankDetails?.accountName || kycDetails?.userId?.fullName || "",
        accountNumber: data.bankDetails?.accountNumber || "",
        ifscCode: data.bankDetails?.ifscCode || "",
        accountType: data.bankDetails?.accountType || data.kycFlowDetails?.bankAccountDetails?.metadata?.kycItemData?.accountType || "savings",
      },
      dematDetails: {
        dpId: data.dematDetails?.dpId || "",
        clientId: data.dematDetails?.clientId || "",
        depository: data.dematDetails?.depository || data.kycFlowDetails?.dematAccountDetails?.metadata?.kycItemData?.depository || "NSDL",
        dan: data.dematDetails?.dan || "",
      }
    });
    setValidationErrors({
      panNumber: "", bankName: "", accountName: "", accountNumber: "", ifscCode: "", dpId: "", clientId: ""
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSaveChanges = async () => {
    // Check for any validation errors before saving
    const hasErrors = Object.values(validationErrors).some(err => err !== "");
    if (hasErrors) {
      toast.error("Please fix the validation errors before saving.", {
        position: "bottom-right",
      });
      return;
    }

    setSaveLoading(true);
    try {
      const response = await kycService.updateKycDetails(id, editFormData);
      if (response.status === "success") {
        toast.success("KYC details updated successfully", {
          position: "bottom-right",
          autoClose: 3000,
        });
        setIsEditing(false);
        await fetchKycDetails();
      } else {
        throw new Error(response.message || "Failed to update KYC details");
      }
    } catch (err) {
      handleApiError(err, "updateError");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    let finalValue = value;
    
    // Auto format specific fields
    if (field === "panNumber" || field === "ifscCode") finalValue = value.toUpperCase();
    if (section === "dematDetails" && field === "dpId" && editFormData.dematDetails.depository === "NSDL") finalValue = value.toUpperCase();

    if (section) {
      setEditFormData(prev => {
        const newSectionData = { ...prev[section], [field]: finalValue };
        
        // Auto-generate DAN if demat fields change
        if (section === "dematDetails") {
          if (field === "depository" || field === "dpId" || field === "clientId") {
            const depo = field === "depository" ? finalValue : prev.dematDetails.depository;
            const d = field === "dpId" ? finalValue : prev.dematDetails.dpId;
            const c = field === "clientId" ? finalValue : prev.dematDetails.clientId;
            
            if (depo === "NSDL" && d && c && d.length === 8 && c.length === 8) {
               newSectionData.dan = `${d}${c}`;
            } else if (depo === "CDSL" && d && c && d.length === 8 && c.length === 8) {
               newSectionData.dan = `${d}${c}`;
            } else {
               newSectionData.dan = ""; // Clear if incomplete
            }
          }
        }
        
        return {
          ...prev,
          [section]: newSectionData
        };
      });
    } else {
      setEditFormData(prev => ({
        ...prev,
        [field]: finalValue
      }));
    }

    // Run validation immediately
    const err = validateField(section, field, finalValue, field === "depository" ? finalValue : null);
    if (err !== undefined && Object.hasOwn(validationErrors, field)) {
       setValidationErrors(prev => ({ ...prev, [field]: err }));
    }
    
    // Cross validate dpId if depository changes
    if (field === "depository") {
      const crossErr = validateField("dematDetails", "dpId", editFormData.dematDetails.dpId, finalValue);
      setValidationErrors(prev => ({ ...prev, dpId: crossErr }));
    }
  };

  // Handle document viewing for both current and verified documents
  const handleViewDocument = (documentType) => {
    if (
      showVerifiedData &&
      kycDetails?.verifiedDoc?.documents?.[documentType]
    ) {
      const verifiedDoc = kycDetails.verifiedDoc.documents[documentType];
      setActiveDocumentModal({
        isOpen: true,
        documentData: verifiedDoc,
        documentName: documentTypes[documentType],
      });
    } else {
      // For current documents, use the existing DocumentViewer
      setActiveDocument(documentType);
    }
  };


  // Action Buttons Component
  const ActionButtons = () => {
    if (!kycDetails || loading) return null;

    const currentStatus = kycDetails.verificationStatus;

    return (
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
        {/* Button Container */}
        <div className="flex flex-wrap gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleCancelEdit}
                disabled={saveLoading}
                className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChanges}
                disabled={saveLoading}
                className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {saveLoading && <Loader className="animate-spin h-4 w-4 mr-2" />}
                Save Changes
              </button>
            </>
          ) : (
            <>
              {/* Edit Button */}
              <button
                onClick={handleEditClick}
                disabled={showVerifiedData}
                className="px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 font-medium text-xs sm:text-sm"
              >
                Edit
              </button>

              {/* Mark In Review Button */}
              <button
                onClick={() => handleStatusChange("in-review")}
                disabled={inReviewLoading || currentStatus === "in-review"}
                className={`px-3 py-2 text-white rounded flex items-center space-x-1 sm:space-x-2 ${currentStatus === "in-review"
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  }`}
              >
                {inReviewLoading ? (
                  <Loader className="animate-spin h-4 w-4 sm:h-4 sm:w-4" />
                ) : (
                  <Eye className="h-4 w-4 sm:h-4 sm:w-4" />
                )}
                <span className="text-xs sm:text-sm">Mark In Review</span>
              </button>

              {/* Verify Button */}
              <button
                onClick={() => handleStatusChange("verified")}
                disabled={verifyLoading || currentStatus === "verified"}
                className={`px-3 py-2 text-white rounded flex items-center space-x-1 sm:space-x-2 ${currentStatus === "verified"
                  ? "bg-green-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  }`}
              >
                {verifyLoading ? (
                  <Loader className="animate-spin h-4 w-4 sm:h-4 sm:w-4" />
                ) : (
                  <CheckCircle className="h-4 w-4 sm:h-4 sm:w-4" />
                )}
                <span className="text-xs sm:text-sm">Verify</span>
              </button>

              {/* Reject Button */}
              <button
                onClick={() => handleStatusChange("rejected")}
                disabled={rejectLoading || currentStatus === "rejected"}
                className={`px-3 py-2 text-white rounded flex items-center space-x-1 sm:space-x-2 ${currentStatus === "rejected"
                  ? "bg-red-400 cursor-not-allowed"
                  : "bg-red-600 hover:bg-red-700 disabled:opacity-50"
                  }`}
              >
                {rejectLoading ? (
                  <Loader className="animate-spin h-4 w-4 sm:h-4 sm:w-4" />
                ) : (
                  <AlertTriangle className="h-4 w-4 sm:h-4 sm:w-4" />
                )}
                <span className="text-xs sm:text-sm">Reject</span>
              </button>
            </>
          )}
        </div>

        {/* Current Status Display */}
        <div className="flex items-center space-x-2 mt-2 sm:mt-0">
          <span className="text-xs sm:text-sm text-gray-600">Status:</span>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
              currentStatus
            )}`}
          >
            {currentStatus || "pending"}
          </span>
        </div>
      </div>
    );
  };

  const currentData = getCurrentData();

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">KYC Details</h1>

        {/* Action Buttons */}
        <ActionButtons />
      </div>

      {/* Rejection Comment Modal */}
      <RejectionCommentModal
        isOpen={commentModalOpen}
        onClose={() => {
          setCommentModalOpen(false);
          setRejectionComment("");
        }}
        onReject={(comment) => {
          // Update the parent's comment state before calling reject
          setRejectionComment(comment);
          handleRejectWithComment(comment);
        }}
        isSubmitting={rejectLoading}
        initialComment={rejectionComment}
      />

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        isOpen={activeDocumentModal.isOpen}
        onClose={() =>
          setActiveDocumentModal({
            isOpen: false,
            documentData: null,
            documentName: "",
          })
        }
        documentData={activeDocumentModal.documentData}
        documentName={activeDocumentModal.documentName}
      />

      {/* Error message display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p>{error}</p>
          <button
            className="font-bold underline ml-2"
            onClick={fetchKycDetails}
          >
            Try again
          </button>
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
        </div>
      )}

      {/* KYC Details Content */}
      {!loading && kycDetails && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Status Bar */}
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center bg-gray-50 p-4 border-b gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center">
                <span className="mr-2 font-medium text-sm">Status:</span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
                    kycDetails.verificationStatus
                  )}`}
                >
                  {kycDetails.verificationStatus || "pending"}
                </span>
              </div>

              {/* Document Version Info */}
              <div className="flex items-center text-sm text-gray-600">
                <span className="mr-2">Version:</span>
                <span className="font-medium">
                  {kycDetails.documentVersion || 1}
                </span>
              </div>

              {/* Modified After Verification Warning */}
              {kycDetails.isModifiedAfterVerification && (
                <div className="flex items-center text-orange-600">
                  <AlertTriangle size={16} className="mr-1" />
                  <span className="text-sm font-medium">
                    Modified After Verification
                  </span>
                </div>
              )}
            </div>

            {/* Data View Toggle */}
            {kycDetails.verifiedDoc && (
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    View Mode:
                  </label>
                  <div className="inline-flex p-1 bg-gray-200 rounded-lg">
                    <button
                      onClick={() => setShowVerifiedData(false)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${!showVerifiedData
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-gray-700 hover:text-gray-900"
                        }`}
                    >
                      Current
                    </button>
                    <button
                      onClick={() => setShowVerifiedData(true)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-all flex items-center ${showVerifiedData
                        ? "bg-green-600 text-white shadow-sm"
                        : "text-gray-700 hover:text-gray-900"
                        }`}
                    >
                      <CheckCircle size={14} className="mr-1" />
                      Verified
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Data View Indicator */}
          {kycDetails.verifiedDoc && (
            <div
              className={`px-4 py-2 text-sm font-medium ${showVerifiedData
                ? "bg-green-50 text-green-800 border-b border-green-200"
                : "bg-blue-50 text-blue-800 border-b border-blue-200"
                }`}
            >
              {showVerifiedData ? (
                <div className="flex items-center">
                  <CheckCircle size={16} className="mr-2" />
                  Showing verified data snapshot from{" "}
                  {formatDate(kycDetails.verifiedDoc.verifiedAt)}
                </div>
              ) : (
                <div className="flex items-center">
                  <Eye size={16} className="mr-2" />
                  Showing current data (last updated:{" "}
                  {formatDate(kycDetails.updatedAt)})
                </div>
              )}
            </div>
          )}

          <div className="p-6">
            {/* User Information */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 pb-2 border-b flex items-center">
                <User className="mr-2" size={20} />
                User Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Full Name
                  </h3>
                  <p className="mt-1">
                    {kycDetails.user?.name ||
                      currentData?.kycFlowDetails?.idDetails?.metadata
                        ?.kycItemData?.fullName ||
                      "N/A"}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">User ID</h3>
                  <p className="mt-1">{kycDetails.user?.id || "N/A"}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Email</h3>
                  <p className="mt-1">{kycDetails.user?.email || "N/A"}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Mobile Number
                  </h3>
                  <p className="mt-1">{kycDetails.user?.mobileNo || "N/A"}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    PAN Number
                  </h3>
                  {isEditing ? (
                    <div>
                      <input
                        type="text"
                        maxLength={10}
                        value={editFormData.panNumber}
                        onChange={(e) => handleInputChange(null, 'panNumber', e.target.value)}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 ${validationErrors.panNumber ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {validationErrors.panNumber && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.panNumber}</p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1 font-mono text-blue-600">
                      {currentData?.panNumber || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Date of Birth
                  </h3>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editFormData.dateOfBirth}
                      onChange={(e) => handleInputChange(null, 'dateOfBirth', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                    />
                  ) : (
                    <p className="mt-1">
                      {formatDateOnly(
                        currentData?.dateOfBirth ||
                        currentData?.kycFlowDetails?.idDetails?.metadata
                          ?.kycItemData?.dateOfBirth
                      ) || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Nationality
                  </h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editFormData.nationality}
                      onChange={(e) => handleInputChange(null, 'nationality', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                    />
                  ) : (
                    <p className="mt-1">{currentData?.nationality || "N/A"}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 pb-2 border-b">
                Address
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Address Line 1
                  </h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editFormData.address.line1}
                      onChange={(e) => handleInputChange('address', 'line1', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                    />
                  ) : (
                    <p className="mt-1">{currentData?.address?.line1 || "N/A"}</p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Address Line 2
                  </h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editFormData.address.line2}
                      onChange={(e) => handleInputChange('address', 'line2', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                    />
                  ) : (
                    <p className="mt-1">{currentData?.address?.line2 || "N/A"}</p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">City</h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editFormData.address.city}
                      onChange={(e) => handleInputChange('address', 'city', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                    />
                  ) : (
                    <p className="mt-1">{currentData?.address?.city || "N/A"}</p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">State</h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editFormData.address.state}
                      onChange={(e) => handleInputChange('address', 'state', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                    />
                  ) : (
                    <p className="mt-1">{currentData?.address?.state || "N/A"}</p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Country</h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editFormData.address.country}
                      onChange={(e) => handleInputChange('address', 'country', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                    />
                  ) : (
                    <p className="mt-1">
                      {currentData?.address?.country || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Pincode</h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editFormData.address.pincode}
                      onChange={(e) => handleInputChange('address', 'pincode', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                    />
                  ) : (
                    <p className="mt-1 font-mono">
                      {currentData?.address?.pincode || "N/A"}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Bank Details */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 pb-2 border-b">
                Bank Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Bank Name
                  </h3>
                  {isEditing ? (
                    <div>
                      <input
                        type="text"
                        value={editFormData.bankDetails.bankName}
                        onChange={(e) => handleInputChange('bankDetails', 'bankName', e.target.value)}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 ${validationErrors.bankName ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {validationErrors.bankName && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.bankName}</p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1">
                      {currentData?.bankDetails?.bankName || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Account Name
                  </h3>
                  {isEditing ? (
                    <div>
                      <input
                        type="text"
                        value={editFormData.bankDetails.accountName}
                        onChange={(e) => handleInputChange('bankDetails', 'accountName', e.target.value)}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 ${validationErrors.accountName ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {validationErrors.accountName && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.accountName}</p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1">
                      {currentData?.bankDetails?.accountName ||
                        kycDetails.userId?.fullName ||
                        "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Account Number
                  </h3>
                  {isEditing ? (
                    <div>
                      <input
                        type="text"
                        maxLength={18}
                        value={editFormData.bankDetails.accountNumber}
                        onChange={(e) => handleInputChange('bankDetails', 'accountNumber', e.target.value.replace(/\D/g, ''))}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 ${validationErrors.accountNumber ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {validationErrors.accountNumber && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.accountNumber}</p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1 font-mono text-blue-600">
                      {currentData?.bankDetails?.accountNumber || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    IFSC Code
                  </h3>
                  {isEditing ? (
                    <div>
                      <input
                        type="text"
                        maxLength={11}
                        value={editFormData.bankDetails.ifscCode}
                        onChange={(e) => handleInputChange('bankDetails', 'ifscCode', e.target.value)}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 uppercase ${validationErrors.ifscCode ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {validationErrors.ifscCode && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.ifscCode}</p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1 font-mono">
                      {currentData?.bankDetails?.ifscCode || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Account Type
                  </h3>
                  {isEditing ? (
                    <select
                      value={editFormData.bankDetails.accountType}
                      onChange={(e) => handleInputChange('bankDetails', 'accountType', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                    >
                      <option value="savings">Savings</option>
                      <option value="current">Current</option>
                      <option value="salary">Salary</option>
                      <option value="nri">NRI</option>
                    </select>
                  ) : (
                    <p className="mt-1 capitalize">
                      {currentData?.bankDetails?.accountType ||
                        currentData?.kycFlowDetails?.bankAccountDetails?.metadata
                          ?.kycItemData?.accountType ||
                        "N/A"}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Demat Details */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 pb-2 border-b">
                Demat Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">DP ID</h3>
                  {isEditing ? (
                    <div>
                      <input
                        type="text"
                        maxLength={editFormData.dematDetails.depository === "NSDL" ? 8 : 8}
                        value={editFormData.dematDetails.dpId}
                        onChange={(e) => handleInputChange('dematDetails', 'dpId', editFormData.dematDetails.depository === "CDSL" ? e.target.value.replace(/\D/g, '') : e.target.value)}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 ${validationErrors.dpId ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {validationErrors.dpId && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.dpId}</p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1 font-mono">
                      {currentData?.dematDetails?.dpId || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Client ID
                  </h3>
                  {isEditing ? (
                    <div>
                      <input
                        type="text"
                        maxLength={8}
                        value={editFormData.dematDetails.clientId}
                        onChange={(e) => handleInputChange('dematDetails', 'clientId', e.target.value.replace(/\D/g, ''))}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 ${validationErrors.clientId ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {validationErrors.clientId && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.clientId}</p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1 font-mono">
                      {currentData?.dematDetails?.clientId || "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Depository
                  </h3>
                  {isEditing ? (
                    <select
                      value={editFormData.dematDetails.depository}
                      onChange={(e) => handleInputChange('dematDetails', 'depository', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                    >
                      <option value="NSDL">NSDL</option>
                      <option value="CDSL">CDSL</option>
                    </select>
                  ) : (
                    <p className="mt-1">
                      {currentData?.dematDetails?.depository ||
                        currentData?.kycFlowDetails?.dematAccountDetails?.metadata
                          ?.kycItemData?.depository ||
                        "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    DAN (Demat Account Number)
                  </h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editFormData.dematDetails.dan}
                      onChange={(e) => handleInputChange('dematDetails', 'dan', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                    />
                  ) : (
                    <p className="mt-1 font-mono text-blue-600">
                      {currentData?.dematDetails?.dan || "N/A"}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* KYC Flow Details */}
            {currentData?.kycFlowDetails && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4 pb-2 border-b flex items-center">
                  <FileText className="mr-2" size={20} />
                  KYC Flow Details
                </h2>

                {/* ID Details */}
                {currentData.kycFlowDetails.idDetails && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-lg font-medium mb-3 text-gray-700">
                      Identity Verification
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Document Type
                        </h4>
                        <p className="mt-1">
                          {currentData.kycFlowDetails.idDetails.documentType ||
                            "N/A"}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Flow Type
                        </h4>
                        <p className="mt-1">
                          {currentData.kycFlowDetails.idDetails.flowType ||
                            "N/A"}
                        </p>
                      </div>
                      {currentData.kycFlowDetails.idDetails.metadata
                        ?.kycItemData && (
                          <>
                            <div>
                              <h4 className="text-sm font-medium text-gray-500">
                                PAN Number
                              </h4>
                              <p className="mt-1 font-mono text-blue-600">
                                {currentData.kycFlowDetails.idDetails.metadata
                                  .kycItemData.panNumber || "N/A"}
                              </p>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-500">
                                Full Name
                              </h4>
                              <p className="mt-1">
                                {currentData.kycFlowDetails.idDetails.metadata
                                  .kycItemData.fullName || "N/A"}
                              </p>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-500">
                                Date of Birth
                              </h4>
                              <p className="mt-1">
                                {formatDateOnly(
                                  currentData.kycFlowDetails.idDetails.metadata
                                    .kycItemData.dateOfBirth
                                ) || "N/A"}
                              </p>
                            </div>
                          </>
                        )}
                    </div>
                  </div>
                )}

                {/* Bank Account Details */}
                {currentData.kycFlowDetails.bankAccountDetails && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-lg font-medium mb-3 text-gray-700">
                      Bank Account Verification
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Document Type
                        </h4>
                        <p className="mt-1">
                          {currentData.kycFlowDetails.bankAccountDetails
                            .documentType || "N/A"}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Flow Type
                        </h4>
                        <p className="mt-1">
                          {currentData.kycFlowDetails.bankAccountDetails
                            .flowType || "N/A"}
                        </p>
                      </div>
                      {currentData.kycFlowDetails.bankAccountDetails.metadata
                        ?.kycItemData && (
                          <>
                            <div>
                              <h4 className="text-sm font-medium text-gray-500">
                                IFSC Code
                              </h4>
                              <p className="mt-1 font-mono">
                                {currentData.kycFlowDetails.bankAccountDetails
                                  .metadata.kycItemData.ifsc || "N/A"}
                              </p>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-500">
                                Account Type
                              </h4>
                              <p className="mt-1 capitalize">
                                {currentData.kycFlowDetails.bankAccountDetails
                                  .metadata.kycItemData.accountType || "N/A"}
                              </p>
                            </div>
                          </>
                        )}
                    </div>
                  </div>
                )}

                {/* Demat Account Details */}
                {currentData.kycFlowDetails.dematAccountDetails && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-lg font-medium mb-3 text-gray-700">
                      Demat Account Verification
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Document Type
                        </h4>
                        <p className="mt-1">
                          {currentData.kycFlowDetails.dematAccountDetails
                            .documentType || "N/A"}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Flow Type
                        </h4>
                        <p className="mt-1">
                          {currentData.kycFlowDetails.dematAccountDetails
                            .flowType || "N/A"}
                        </p>
                      </div>
                      {currentData.kycFlowDetails.dematAccountDetails.metadata
                        ?.kycItemData && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-500">
                              Depository
                            </h4>
                            <p className="mt-1">
                              {currentData.kycFlowDetails.dematAccountDetails
                                .metadata.kycItemData.depository || "N/A"}
                            </p>
                          </div>
                        )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Documents Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Documents</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
                {Object.entries(documentTypes).map(([key, name]) => {
                  const currentDoc = kycDetails.documents?.[key];
                  const verifiedDoc = kycDetails.verifiedDoc?.documents?.[key];

                  // For current documents - check if URL exists or hasData is true
                  const hasCurrentDocument =
                    currentDoc &&
                    (currentDoc.documentUrl ||
                      currentDoc.hasData ||
                      currentDoc.data ||
                      currentDoc.contentType);

                  // For verified documents - check if binary data exists
                  const hasVerifiedDocument =
                    verifiedDoc &&
                    (verifiedDoc.data || verifiedDoc.contentType);

                  // Determine which document to show based on current view
                  const documentToShow = showVerifiedData
                    ? verifiedDoc
                    : currentDoc;
                  const hasDocument = showVerifiedData
                    ? hasVerifiedDocument
                    : hasCurrentDocument;

                  return (
                    <div
                      key={key}
                      className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-medium text-sm">{name}</h4>
                        <div className="flex flex-col space-y-1">
                          {showVerifiedData ? (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs 
                                ${hasVerifiedDocument
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800"
                                }`}
                            >
                              {hasVerifiedDocument
                                ? "Available"
                                : "Not Available"}
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs 
                                ${hasCurrentDocument
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800"
                                }`}
                            >
                              {hasCurrentDocument ? "Uploaded" : "Missing"}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Document metadata */}
                      {documentToShow && (
                        <div className="text-xs text-gray-500 mb-2 space-y-1">
                          {documentToShow.contentType && (
                            <div className="flex items-center">
                              <span className="font-medium mr-1">Type:</span>
                              <span className="bg-gray-100 px-1 rounded text-xs">
                                {documentToShow.contentType}
                              </span>
                            </div>
                          )}
                          {documentToShow.uploadedAt && (
                            <div className="flex items-center">
                              <Calendar size={12} className="mr-1" />
                              <span>
                                {formatDate(documentToShow.uploadedAt)}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {hasDocument && (
                        <button
                          onClick={() => handleViewDocument(key)}
                          className="w-full mt-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm transition-colors flex items-center justify-center"
                        >
                          <Eye size={14} className="mr-1" />
                          {showVerifiedData ? "View Verified" : "View Document"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Show DocumentViewer only for current documents */}
              {activeDocument && !showVerifiedData && (
                <div className="mt-6 border-t pt-6 relative">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">
                      Viewing: {documentTypes[activeDocument]}
                    </h3>
                    <button
                      onClick={() => setActiveDocument(null)}
                      className="flex items-center text-gray-500 hover:text-gray-700"
                      title="Close document viewer"
                    >
                      <X size={20} />
                      <span className="ml-1">Close</span>
                    </button>
                  </div>
                  <DocumentViewer
                    kycId={kycDetails._id}
                    documentType={activeDocument}
                    documentName={documentTypes[activeDocument]}
                  />
                </div>
              )}
            </div>

            {/* Verification Info */}
            <div className="mb-4">
              <h2 className="text-xl font-semibold mb-4 pb-2 border-b flex items-center">
                <CheckCircle className="mr-2" size={20} />
                Verification Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">KYC ID</h3>
                  <p className="mt-1 font-mono text-blue-600">
                    {kycDetails._id || "N/A"}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    KYC Status
                  </h3>
                  <p className="mt-1">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
                        kycDetails.verificationStatus
                      )}`}
                    >
                      {kycDetails.verificationStatus || "pending"}
                    </span>
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Submission Date
                  </h3>
                  <p className="mt-1 flex items-center">
                    <Calendar size={14} className="mr-1 text-gray-400" />
                    {formatDate(kycDetails.createdAt)}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Last Updated
                  </h3>
                  <p className="mt-1 flex items-center">
                    <Calendar size={14} className="mr-1 text-gray-400" />
                    {formatDate(kycDetails.updatedAt)}
                  </p>
                </div>
                {kycDetails.verifiedAt && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Verification Date
                    </h3>
                    <p className="mt-1 flex items-center text-green-600">
                      <CheckCircle size={14} className="mr-1" />
                      {formatDate(kycDetails.verifiedAt)}
                    </p>
                  </div>
                )}
                {kycDetails.verifierId && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Verified By
                    </h3>
                    <p className="mt-1 flex items-center">
                      <User size={14} className="mr-1 text-gray-400" />
                      {kycDetails.verifierId}
                    </p>
                  </div>
                )}
                {kycDetails.reviewerId && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Reviewer
                    </h3>
                    <p className="mt-1 flex items-center">
                      <User size={14} className="mr-1 text-gray-400" />
                      {kycDetails.reviewerId}
                    </p>
                  </div>
                )}
                {kycDetails.reviewStartedAt && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Review Started At
                    </h3>
                    <p className="mt-1 flex items-center text-blue-600">
                      <Eye size={14} className="mr-1" />
                      {formatDate(kycDetails.reviewStartedAt)}
                    </p>
                  </div>
                )}
                {kycDetails.rejectionReason && (
                  <div className="md:col-span-2">
                    <h3 className="text-sm font-medium text-gray-500">
                      Rejection Reason
                    </h3>
                    <div className="mt-1 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-red-600 flex items-start">
                        <AlertTriangle
                          size={14}
                          className="mr-2 mt-0.5 flex-shrink-0"
                        />
                        {kycDetails.rejectionReason}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Information Summary */}
            {(kycDetails.documentVersion > 1 ||
              kycDetails.isModifiedAfterVerification) && (
                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h3 className="text-sm font-medium text-yellow-800 mb-2 flex items-center">
                    <AlertCircle size={16} className="mr-2" />
                    Document History & Modifications
                  </h3>
                  <div className="text-sm text-yellow-700 space-y-1">
                    {kycDetails.documentVersion > 1 && (
                      <p>
                        • This KYC has been updated {kycDetails.documentVersion}{" "}
                        times
                      </p>
                    )}
                    {kycDetails.isModifiedAfterVerification && (
                      <p>
                        • Documents have been modified after initial verification
                      </p>
                    )}
                    {kycDetails.verifiedDoc && (
                      <p>
                        • Use the "Verified Data" toggle above to compare with the
                        originally verified information
                      </p>
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserKycView;
